// stores/useAccommodation.ts
import { create } from "zustand";
import {
  TripLodging,
  HotelInitial,
  ViewLodging,
  AccommodationListing,
  Location,
  Trip,
} from "@/types";
import useTripStore from "./useTrip";
import { convertHotelIntoViewLodging } from "@/lib/accommodation/hotels";
import { convertHomeStayIntoViewLodging } from "@/lib/accommodation/homes";
import logger from "@/utils/logger";

interface AccommodationStore {
  view: "map" | "booking";
  setView: (view: "map" | "booking") => void;
  activeAccommodationTab: "hotels" | "home-stays" | "custom";
  setActiveAccommodationTab: (tab: "hotels" | "home-stays" | "custom") => void;
  lodgings: TripLodging[];
  setLodgings: (lodgings: TripLodging[]) => void;
  viewingLodging: ViewLodging | null;
  setViewingLodging: (lodging: ViewLodging | null) => void;
  coreLodging: HotelInitial | AccommodationListing | TripLodging | null;
  setCoreLodging: (
    lodging: HotelInitial | AccommodationListing | TripLodging | null
  ) => void;
  selectedLodging: TripLodging | null;
  addLodging: (lodging: TripLodging) => void;
  removeLodging: (lodgingId: string | number) => void;
  addingLodging: { isAdding: boolean; lodgingId: string | null };
  setAddingLodging: (isAdding: boolean, lodgingId: string | null) => void;
  setSelectedLodging: (lodging: TripLodging | null) => void;
  getLocationLodgings: () => TripLodging[];
  getMainLocationLodging: (selectedLocationId: string) => TripLodging | null;
  responseHotels: HotelInitial[];
  setResponseHotels: (hotels: HotelInitial[]) => void;
  responseHomeStays: AccommodationListing[];
  setResponseHomeStays: (homeStays: AccommodationListing[]) => void;
  showSearchThisAreaButton: boolean;
  setShowSearchThisAreaButton: (show: boolean) => void;
  shouldSearchArea: boolean;
  setShouldSearchArea: (should: boolean) => void;
  isFetchingHotels: boolean;
  setIsFetchingHotels: (isFetching: boolean) => void;
  isFetchingHomeStays: boolean;
  setIsFetchingHomeStays: (isFetching: boolean) => void;
  viewLodging: ViewLodging | null;
  setViewLodging: (lodging: ViewLodging | null) => void;
  setMainLodging: (lodging: TripLodging) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  setTotalPages: (total: number) => void;
  lastAddedHotel: TripLodging | null;
  setLastAddedHotel: (hotel: TripLodging | null) => void;
  showAddNotification: boolean;
  setShowAddNotification: (show: boolean) => void;
  isMainLodgingModalOpen: boolean;
  setIsMainLodgingModalOpen: (isOpen: boolean) => void;
  selectedRating: number | null;
  selectedPriceRange: string | null;
  setSelectedRating: (rating: number | null) => void;
  setSelectedPriceRange: (range: string | null) => void;
  clearFilters: () => void;
  handleModalOpenMainLodging: (lodging: HotelInitial) => void;
  handleModalOpenHomeStay: (lodging: AccommodationListing) => void;
  handleModalOpenTripLodging: (lodging: TripLodging) => void;
  getAccommodationLength: (tripData: Trip) => number;
}
const useAccommodationStore = create<AccommodationStore>((set, get) => ({
  view: "map",
  setView: (view) => set({ view }),
  viewingLodging: null,
  setViewingLodging: (lodging) => set({ viewingLodging: lodging }),
  lodgings: [],
  setLodgings: (lodgings) => set({ lodgings }),
  selectedLodging: null,
  setSelectedLodging: (lodging) => set({ selectedLodging: lodging }),
  coreLodging: null,
  setCoreLodging: (lodging) => set({ coreLodging: lodging }),
  responseHotels: [],
  setResponseHotels: (hotels) => set({ responseHotels: hotels }),
  responseHomeStays: [],
  setResponseHomeStays: (homeStays) => set({ responseHomeStays: homeStays }),
  showSearchThisAreaButton: false,
  setShowSearchThisAreaButton: (show: boolean) =>
    set({ showSearchThisAreaButton: show }),
  shouldSearchArea: false,
  setShouldSearchArea: (should: boolean) => set({ shouldSearchArea: should }),
  isFetchingHotels: false,
  setIsFetchingHotels: (isFetching: boolean) =>
    set({ isFetchingHotels: isFetching }),
  isFetchingHomeStays: false,
  setIsFetchingHomeStays: (isFetching: boolean) =>
    set({ isFetchingHomeStays: isFetching }),
  addingLodging: { isAdding: false, lodgingId: null },
  setAddingLodging: (isAdding: boolean, lodgingId: string | null) =>
    set({ addingLodging: { isAdding, lodgingId } }),
  viewLodging: null,
  setViewLodging: (lodging) => set({ viewLodging: lodging }),
  activeAccommodationTab: "hotels",
  setActiveAccommodationTab: (tab: "hotels" | "home-stays" | "custom") =>
    set({ activeAccommodationTab: tab }),
  currentPage: 0,
  setCurrentPage: (page) => set({ currentPage: page }),
  totalPages: 0,
  setTotalPages: (total) => set({ totalPages: total }),
  lastAddedHotel: null,
  setLastAddedHotel: (hotel) => set({ lastAddedHotel: hotel }),
  showAddNotification: false,
  setShowAddNotification: (show) => set({ showAddNotification: show }),
  isMainLodgingModalOpen: false,
  setIsMainLodgingModalOpen: (isOpen) =>
    set({ isMainLodgingModalOpen: isOpen }),
  selectedRating: null,
  selectedPriceRange: null,
  setSelectedRating: (rating) => set({ selectedRating: rating }),
  setSelectedPriceRange: (range) => set({ selectedPriceRange: range }),
  clearFilters: () =>
    set({
      selectedRating: null,
      selectedPriceRange: null,
    }),

  addLodging: (lodging: TripLodging) => {
    const { selectedLocation, tripData, updateLocation } =
      useTripStore.getState();
    if (!selectedLocation || !tripData) return;

    const existingLodging = selectedLocation.accommodation.temp?.find(
      (l) => l.id === lodging.id
    );
    if (existingLodging) return;

    set((state) => ({
      lodgings: [...state.lodgings, lodging],
    }));

    updateLocation(selectedLocation.id, {
      accommodation: {
        main: selectedLocation.accommodation.main || lodging,
        temp: [...(selectedLocation.accommodation.temp || []), lodging],
      },
    });

    logger.log("addLodging", get().lodgings);
  },

  removeLodging: (lodgingId: string | number) => {
    const { selectedLocation, updateLocation } = useTripStore.getState();
    if (!selectedLocation) return;

    set((state) => ({
      lodgings: state.lodgings.filter((lodging) => lodging.id !== lodgingId),
    }));

    const isMainLodging = selectedLocation.accommodation.main?.id === lodgingId;

    updateLocation(selectedLocation.id, {
      accommodation: {
        main: isMainLodging ? null : selectedLocation.accommodation.main,
        temp:
          selectedLocation.accommodation.temp?.filter(
            (lodging) => lodging.id !== lodgingId
          ) || null,
      },
    });
  },

  handleModalOpenTripLodging: (lodging: TripLodging) => {
    const viewLodging: ViewLodging = {
      id: lodging.id,
      name: lodging.name,
      type: lodging.type,
      mainImageURL: lodging.mainImageURL,
      latitude: lodging.coordinates.latitude,
      longitude: lodging.coordinates.longitude,
      description: lodging.description,
      moreInfo: {
        reviewCount: lodging.moreInfo?.reviewCount ?? 0,
        reviewScore: lodging.moreInfo?.reviewScore ?? 0,
      },
    };
    get().setCoreLodging(lodging); // Add this line
    get().setViewLodging(viewLodging);
    get().setViewingLodging(viewLodging);
    get().setIsMainLodgingModalOpen(true);
  },

  getMainLocationLodging: (selectedLocationId: string) => {
    const { tripData } = useTripStore.getState();
    if (!tripData) return null;
    const location = tripData.locations.find(
      (location: Location) => location.id === selectedLocationId
    );
    return location?.accommodation.main || null;
  },

  getLocationLodgings: () => {
    const { selectedLocation } = useTripStore.getState();
    if (!selectedLocation) return [];
    return selectedLocation.accommodation.temp || [];
  },

  setMainLodging: (lodging: TripLodging) => {
    const { updateLocation, selectedLocation } = useTripStore.getState();
    if (selectedLocation) {
      updateLocation(selectedLocation.id, {
        accommodation: {
          main: lodging,
          temp: selectedLocation.accommodation.temp || null,
        },
      });

      useTripStore
        .getState()
        .createLocationUpdate(
          selectedLocation.waytora_id,
          selectedLocation.id,
          "accommodation_added",
          {
            lodging,
            timeZone: selectedLocation.date.time_zone,
          }
        );
    }
  },

  handleModalOpenMainLodging: (lodging: HotelInitial) => {
    const tripLodging = convertHotelIntoViewLodging(lodging);
    get().setCoreLodging(lodging);
    get().setViewLodging(tripLodging);
    get().setViewingLodging(tripLodging);
    get().setIsMainLodgingModalOpen(true);
  },

  handleModalOpenHomeStay: (lodging: AccommodationListing) => {
    const tripLodging = convertHomeStayIntoViewLodging(lodging);
    get().setCoreLodging(lodging);
    get().setViewLodging(tripLodging);
    get().setViewingLodging(tripLodging);
    get().setIsMainLodgingModalOpen(true);
  },

  getAccommodationLength: (tripData: Trip) => {
    return tripData.locations.reduce((acc: number, location: Location) => {
      return acc + (location.accommodation.temp?.length || 0);
    }, 0);
  },
}));

export default useAccommodationStore;
